import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ACTIVITY_THINKING, AgentChatMessage, agoLabel, nowMinuteMs } from "./agent-chat.models";
import { AgentChatService } from "./agent-chat.service";
import { AgentViewContextService } from "./agent-view-context.service";
import { renderAgentMarkdown, renderAgentMarkdownStreaming } from "./agent-message-render";
import { environment } from "../../environment";
import { WalkthroughCardComponent } from "../walkthroughs/walkthrough-card.component";
import { VegaAvatarComponent } from "./vega-avatar.component";
import { MemoryApiService, flattenAgentMarkup } from "@app/common/services/memory-api.service";
import { MemoryDialogService } from "@app/common/services/memory-dialog.service";
import { PortfolioStateService } from "@app/parse-analyze-track/services/portfolio-state.service";
import { ScheduleApiService } from "@app/common/services/schedule-api.service";
import { ToastService } from "@app/common/services/toast.service";
import { defaultDailySchedule } from "@app/parse-analyze-track/components/schedules/schedule-format";
import { Router } from "@angular/router";

@Component({
  selector: "tp-agent-message",
  standalone: true,
  imports: [MatIconModule, MatTooltipModule, WalkthroughCardComponent, VegaAvatarComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (message().role === "user") {
      <div class="msg msg--user">
        <div class="msg-user-col">
          <div class="bubble">{{ message().text }}</div>
          @if (timeLabel(); as time) {
            <span class="msg-time msg-time--user">{{ time }}</span>
          }
        </div>
        @if (message().text) {
          <div class="msg-actions msg-actions--user">
            <button
              class="action-btn"
              matTooltip="Publish to your notes"
              (click)="commitToMemory()"
              aria-label="Publish this message to your notes"
            >
              <mat-icon>bookmark_add</mat-icon>
            </button>
            <button
              class="action-btn"
              matTooltip="Repeat on a schedule"
              (click)="repeatOnSchedule()"
              aria-label="Run this question on a daily schedule"
            >
              <mat-icon>{{ scheduled() ? "check" : "event_repeat" }}</mat-icon>
            </button>
          </div>
        }
      </div>
    } @else {
      <div class="msg msg--assistant" [class.msg--continued]="!showSpeaker()">
        <!-- Who is talking, as a slim row over the reply (no gutter: the text keeps the full width). -->
        @if (showSpeaker()) {
          <div class="msg-speaker">
            <tp-vega-avatar [size]="20" />
            <span class="msg-speaker-name">Vega</span>
          </div>
        }
        @if (showRaw()) {
          <pre class="raw">{{ message().text }}</pre>
        } @else {
          <div
            class="rendered"
            [innerHTML]="renderedHtml()"
            (click)="onContentClick($event)"
            (keydown.enter)="onContentClick($event)"
          ></div>
        }
        @if (message().memory; as memory) {
          <div class="memory-chip" role="status" data-agent-help-id="global.memory-receipt" data-agent-help-label="Memory receipt">
            @if (memory.saved; as saved) {
              <mat-icon class="memory-chip-icon">{{ memory.undone ? "undo" : "save" }}</mat-icon>
              @if (memory.undone) {
                <span>Removed from memory</span>
              } @else {
                <span>Saved to memory → <strong>{{ saved.topicName }}</strong> ({{ saved.fidelity }})</span>
                <button class="memory-chip-btn" type="button" (click)="undoMemory()" [disabled]="memoryBusy()">Undo</button>
              }
            } @else if (memory.newTopic; as proposal) {
              <mat-icon class="memory-chip-icon">{{ memory.created ? "save" : "create_new_folder" }}</mat-icon>
              @if (memory.created; as created) {
                <span>Saved to memory → <strong>{{ created.topicName }}</strong> (new topic, {{ created.fidelity }})</span>
              } @else {
                <span>New topic proposed: <strong>{{ proposal.name }}</strong></span>
                <button class="memory-chip-btn" type="button" (click)="createTopicAndSave()" [disabled]="memoryBusy()">Create topic and save</button>
              }
            }
          </div>
        }
        @if (message().walkthrough; as walkthrough) {
          <tp-walkthrough-card [walkthroughId]="walkthrough.id" />
        }
        @if (activityLabel(); as label) {
          <div class="thinking">
            <span class="dot"></span><span class="dot"></span><span class="dot"></span>
            <span class="thinking-label" aria-live="polite">{{ label }}</span>
          </div>
        }
        @if (!message().streaming && message().text) {
          <div class="msg-actions">
            <button
              class="action-btn"
              matTooltip="Copy"
              (click)="copy()"
              aria-label="Copy message"
            >
              <mat-icon>{{ copied() ? "check" : "content_copy" }}</mat-icon>
            </button>
            <button
              class="action-btn"
              matTooltip="Publish to your notes"
              (click)="commitToMemory()"
              aria-label="Publish this message to your notes"
            >
              <mat-icon>{{ committed() ? "bookmark_added" : "bookmark_add" }}</mat-icon>
            </button>
            @if (conversationEnd()) {
              <button
                class="action-btn"
                [class.action-btn--saved]="chat.activeThreadSaved()"
                [matTooltip]="chat.activeThreadSaved() ? 'Remove from Saved' : 'Save conversation'"
                [attr.aria-label]="chat.activeThreadSaved() ? 'Remove conversation from Saved' : 'Save conversation'"
                [attr.aria-pressed]="chat.activeThreadSaved()"
                [disabled]="chat.activeThreadSavePending()"
                (click)="chat.toggleActiveThreadSaved()"
              >
                <mat-icon>{{ chat.activeThreadSaved() ? "star" : "star_border" }}</mat-icon>
              </button>
            }
            @if (isDev) {
              <button
                class="action-btn"
                matTooltip="View raw markup"
                (click)="showRaw.set(!showRaw())"
                aria-label="Toggle raw markup"
              >
                <mat-icon>{{ showRaw() ? "visibility" : "code" }}</mat-icon>
              </button>
            }
          </div>
        }
        @if (timeLabel(); as time) {
          <span class="msg-time">{{ time }}</span>
        }
      </div>
    }
  `,
  styleUrls: ["./agent-message.component.scss"],
})
export class AgentMessageComponent {
  readonly message = input.required<AgentChatMessage>();
  /** True only for the final rendered message in the active conversation. */
  readonly conversationEnd = input(false);
  /**
   * Vega's speaker row (avatar + name) over a reply. The list turns it off for
   * a reply that directly follows another Vega message, so a run of them reads
   * as one turn.
   */
  readonly showSpeaker = input(true);
  protected chat = inject(AgentChatService);
  private sanitizer = inject(DomSanitizer);
  private memoryDialog = inject(MemoryDialogService);
  private memoryApi = inject(MemoryApiService);
  private viewContext = inject(AgentViewContextService);
  private portfolioState = inject(PortfolioStateService);
  private scheduleApi = inject(ScheduleApiService);
  private toast = inject(ToastService);
  private router = inject(Router);

  protected readonly isDev = !environment.production;
  protected readonly showRaw = signal(false);
  protected readonly copied = signal(false);
  protected readonly committed = signal(false);
  protected readonly scheduled = signal(false);
  protected readonly memoryBusy = signal(false);
  private scheduling = false;

  /** "now" / "5m" / "3h" beside the actions — ages via the shared minute tick. */
  protected readonly timeLabel = computed(() => {
    const message = this.message();
    return message.streaming ? "" : agoLabel(message.createdAt, nowMinuteMs());
  });

  /**
   * The phase line under a streaming answer — empty string means "show
   * nothing". Covers the whole turn with no unexplained gaps:
   *   - before any tool call, nothing written yet -> the seeded "reading your
   *     question" (or a fallback, if a reconnect lost the seed)
   *   - during a tool call, whether or not text has already been written ->
   *     that tool's label, so a mid-answer call still explains itself
   *   - while text is actually streaming -> hidden; the words are the progress
   *   - turn over -> hidden
   */
  protected readonly activityLabel = computed(() => {
    if (!this.message().streaming) return "";
    const label = this.chat.activity();
    if (label) return label;
    return this.message().text ? "" : ACTIVITY_THINKING;
  });

  protected readonly renderedHtml = computed<SafeHtml>(() => {
    // Rendered HTML is produced by our own sanitizing pipeline
    // (marked → DOMPurify → inert link chips); bypass marks it as trusted.
    // While streaming, in-flight structure (unclosed fences/tokens) renders
    // as a pending placeholder instead of flashing raw markup that later
    // snaps into a card and shifts the layout mid-read.
    const msg = this.message();
    return this.sanitizer.bypassSecurityTrustHtml(
      msg.streaming ? renderAgentMarkdownStreaming(msg.text) : renderAgentMarkdown(msg.text),
    );
  });

  protected onContentClick(event: Event): void {
    const target = event.target as HTMLElement | null;
    const chip = target?.closest?.(".tp-app-link") as HTMLElement | null;
    if (!chip) return;
    event.preventDefault();
    const path = chip.dataset["appPath"];
    if (path) this.chat.navigateAppLink(path, chip.dataset["appQuery"] ?? "");
  }

  protected copy(): void {
    void navigator.clipboard.writeText(this.message().text).then(() => {
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 1500);
    });
  }

  /**
   * Hover-commit: THIS message, flattened to plain text ({{pnl:}}/{{calc:}}
   * tokens and app links carry provenance claims that must not enter
   * storage), opens the shared memory dialog pre-filled and editable — the
   * user reviews exactly what will be stored before saving. The viewing
   * portfolio is auto-captured as soft provenance; no picker interrupts the
   * flow (it's still changeable in the dialog).
   */
  protected commitToMemory(): void {
    const content = flattenAgentMarkup(this.message().text);
    const pidParam = this.viewContext.currentPid();
    const pid = pidParam != null ? Number(pidParam) : null;
    const portfolios = this.portfolioState.all().map((p) => ({ id: p.id, name: p.name }));
    const firstLine = content.split("\n")[0]?.replace(/[#*_`>]/g, "").trim() ?? "";
    this.memoryDialog
      .open({
        mode: "commit",
        prefillContent: content,
        prefillTitle: firstLine.length > 60 ? `${firstLine.slice(0, 57)}…` : firstLine,
        portfolios,
        defaultPortfolioId: Number.isFinite(pid) ? pid : null,
      })
      .afterClosed()
      .subscribe((saved) => {
        if (saved) {
          this.committed.set(true);
          setTimeout(() => this.committed.set(false), 2500);
        }
      });
  }

  /**
   * The receipt chip's Undo: retire the entry the tool just saved (history is
   * kept server-side). The chip flips to "Removed"; the model's sentence stays.
   */
  protected undoMemory(): void {
    const saved = this.message().memory?.saved;
    if (!saved || this.memoryBusy()) return;
    this.memoryBusy.set(true);
    this.memoryApi
      .retireEntry(saved.entryId)
      .then(() => this.chat.patchMessageMemory(this.message().id, { undone: true }))
      .catch((error: Error) => this.toast.error(error.message))
      .finally(() => this.memoryBusy.set(false));
  }

  /**
   * A NEW topic is never created by the model: this click is the consent
   * (propose → review → execute). It creates the topic, then files the entry.
   */
  protected createTopicAndSave(): void {
    const proposal = this.message().memory?.newTopic;
    if (!proposal || this.memoryBusy()) return;
    this.memoryBusy.set(true);
    const portfolioName =
      proposal.portfolioId != null
        ? (this.portfolioState.all().find((p) => p.id === proposal.portfolioId)?.name ?? null)
        : null;
    this.memoryApi
      .createTopic({ name: proposal.name, description: proposal.description, cues: proposal.cues, portfolioId: proposal.portfolioId, portfolioName })
      .then((topic) => this.memoryApi.createEntry({ topicId: topic.id, fidelity: proposal.fidelity, text: proposal.text, tags: proposal.tags }))
      .then((entry) => this.chat.patchMessageMemory(this.message().id, { created: { topicName: proposal.name, fidelity: entry.fidelity } }))
      .catch((error: Error) => this.toast.error(error.message))
      .finally(() => this.memoryBusy.set(false));
  }

  /**
   * Hover-repeat: turn THIS question into a schedule in one click, using the
   * same defaults the schedule dialog opens with (daily 9:00 AM local, Think,
   * new conversation per run). No dialog — the toast's action link takes the
   * user to the Schedules tab to adjust timing. Cap/validation messages from
   * the runtime are relayed verbatim.
   */
  protected repeatOnSchedule(): void {
    if (this.scheduling) return;
    const text = this.message().text.trim();
    if (!text) return;
    this.scheduling = true;
    this.scheduleApi
      .create(defaultDailySchedule(text))
      .then(() => {
        this.scheduled.set(true);
        setTimeout(() => this.scheduled.set(false), 2500);
        this.toast.successWithAction(
          "Scheduled — this will now run daily at 9:00 AM. Change the timing and options in Schedules.",
          "Open",
          () => void this.router.navigate(["/parse-analyze-track/settings", "schedules"]),
        );
      })
      .catch((error: Error) => this.toast.error(error.message))
      .finally(() => {
        this.scheduling = false;
      });
  }
}
